import React, { useMemo, useState } from 'react'

import {
    getForceVideoToLive,
    getInitialPlayerState,
    getRemainingPasses,
    getShouldPlayPreRoll,
    getShow,
    getShowHasStarted,
    getShowPreview,
    getVideo,
    getVideoOnDemand,
    PreviewState,
} from 'redux/slices/liveShow'
import playerStates from './PlayerStates'
import LiveVideo from './LiveVideo/LiveVideo'
import { StateMachine } from 'react-machinery'
import { useSelector } from 'react-redux'
import { CurtainValue, getCurtainValue } from 'redux/slices/curtain'
import { getShowPassList, userHasPurchasedTicket as getUserHasPurchasedTicket } from 'redux/slices/passes'
import { Video } from 'data/Video'
import { Show, VideoOnDemand } from 'data/Show'
import { Unpopulated } from 'data/Populated'
import { Pass } from 'data/Pass'

export interface InteractionPlayerProps {
    remainingPasses: number | null
    showHasStarted: boolean
    video: Video
    show: Show
    previewStarted: boolean
    previewEnded: boolean
    shouldPlayPreRoll: boolean
    userHasPurchasedTicket: boolean
    videoOnDemand: VideoOnDemand | null
    passList: Unpopulated<Pass, 'show'>[]
    forceLive: boolean
    curtainValue: CurtainValue
}

export const InteractionPlayer: React.FC = () => {
    const initialPlayerState = useSelector(getInitialPlayerState)
    const [currentPlayerState, setCurrentPlayerState] = useState(initialPlayerState)

    const show = useSelector(getShow)!
    const video = useSelector(getVideo)
    const passList = useSelector(getShowPassList)
    const remainingPasses = useSelector(getRemainingPasses)
    const showHasStarted = useSelector(getShowHasStarted)
    const videoOnDemand = useSelector(getVideoOnDemand)
    const preview = useSelector(getShowPreview)
    const previewStarted = useMemo(() => preview.state === PreviewState.STARTED, [preview.state])
    const previewEnded = useMemo(() => preview.state === PreviewState.ENDED, [preview.state])
    const shouldPlayPreRoll = useSelector(getShouldPlayPreRoll)
    const userHasPurchasedTicket = useSelector(getUserHasPurchasedTicket)
    const forceLive = useSelector(getForceVideoToLive)
    const curtainValue = useSelector(getCurtainValue)

    if (show.alwaysPlay) {
        //Render the video player
        return (
            <LiveVideo
                passList={passList}
                show={show}
                stateId="AlwaysPlay"
                muted={process.env.NODE_ENV === 'development'}
            />
        )
    }

    const data: InteractionPlayerProps = {
        passList,
        showHasStarted,
        show,
        userHasPurchasedTicket,
        previewStarted,
        previewEnded,
        shouldPlayPreRoll,
        video,
        forceLive,
        curtainValue,
        videoOnDemand,
        remainingPasses,
    }

    return (
        <div data-testid={currentPlayerState}>
            <StateMachine
                getCurrentState={() => currentPlayerState}
                setNewState={newPlayerState => setCurrentPlayerState(newPlayerState)}
                data={data}
                states={playerStates[show.scope]}
            />
        </div>
    )
}

export default React.memo(InteractionPlayer)
